import React, {useCallback, useEffect, useState} from 'react'
import View from './view'
import {Helmet} from 'react-helmet'
import {useAppDispatch} from '../../../redux/configureStore'
import {useAuth} from '../../../components/AuthProvider'
import {
  clearVideoState,
  getVideo,
  selectVideo,
  trackProgress,
} from '../../../redux/videos'
import {toggleErrorDialog} from '../../../redux/config'
import {
  getStudyGuide,
  saveStudyGuideProgress,
  selectStudyGuide,
} from '../../../redux/studyGuides'
import {useParams} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {selectMemberAccess} from '../../../redux/members'
import {getOrganizationCourses} from '../../../redux/courses'

const VideoPage = () => {
  const {studyGuideId, videoId, studyDay} = useParams()

  const [videoEnded, setVideoEnded] = useState(false)

  const studyGuide = useSelector(selectStudyGuide)
  const video = useSelector(selectVideo)
  const access = useSelector(selectMemberAccess)

  const hasAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.videos?.value) === 1
      : false

  const auth = useAuth()
  const dispatch = useAppDispatch()

  const loadStudyGuide = useCallback(async () => {
    try {
      if (studyGuideId) {
        await dispatch(
          getStudyGuide({
            auth,
            studyGuideId,
            studyDay: Number(studyDay),
            keepState: true,
          }),
        )
      }
    } catch (e) {
      await dispatch(
        toggleErrorDialog({
          opened: true,
          error:
            'We were unable to load the video, please retry reloading the page. If issues persist contact our support team',
        }),
      )
    }
  }, [dispatch, studyGuideId, studyDay])

  const loadVideo = useCallback(async () => {
    try {
      if (auth.isAuthenticated && hasAccess) {
        const {type} = await dispatch(
          getVideo({auth, videoId, isSoftware: false}),
        )
        if (type === getVideo.rejected.type) {
          throw new Error('Unable to get video')
        }
      }
    } catch (e) {
      await dispatch(
        toggleErrorDialog({
          opened: true,
          error:
            'We were unable to load the video, please retry reloading the page. If issues persist contact our support team',
        }),
      )
    }
  }, [dispatch, videoId, hasAccess])

  const handleUpdate = async (percent: number, playerDuration?: number) => {
    await dispatch(
      trackProgress({
        auth,
        videoId,
        ts: percent,
        videoLengthUpdate: playerDuration,
        lessonPlanId: studyGuide?.lessonPlanId,
      }),
    )
  }

  const handleSaveSgProgress = async (globalAssetId: number) => {
    if (studyDay) {
      const {type} = await dispatch(
        saveStudyGuideProgress({
          auth,
          globalAssetId,
          studyDay: Number(studyDay),
        }),
      )

      if (type === saveStudyGuideProgress.fulfilled.type) {
        await dispatch(getOrganizationCourses({auth, keepState: true}))
      }
    }
  }

  const clearVideo = async () => {
    await dispatch(clearVideoState())
  }

  const handleChangeVideoEnded = (value: boolean) => {
    clearVideo()
    setVideoEnded(value)
  }

  useEffect(() => {
    loadStudyGuide()
    loadVideo()
    setVideoEnded(false)

    return () => {
      clearVideo()
    }
  }, [loadStudyGuide, loadVideo])

  return (
    <>
      {video && (
        <Helmet>
          <title>{`${video?.title} | Black Spectacles`}</title>
        </Helmet>
      )}
      <View
        videoId={videoId}
        studyGuide={studyGuide}
        video={video}
        hasAccess={hasAccess}
        studyDay={studyDay}
        onUpdate={handleUpdate}
        onSaveSgProgress={handleSaveSgProgress}
        onChangeVideoEnded={handleChangeVideoEnded}
        videoEnded={videoEnded}
      />
    </>
  )
}

export default VideoPage
